/**
 * Breadcrumb trail — captures the last 20 user actions so error reports include
 * "what was happening before the crash."
 *
 * Captured automatically:
 * - Route changes (history.pushState/replaceState/popstate)
 * - User clicks (delegated, captures element tag + label/text + nearest button)
 * - Fetch calls (URL, method, status, duration)
 *
 * Manually addable via `addBreadcrumb({ category: 'custom', message: '...' })`.
 *
 * The buffer is read by the logger when an error is reported.
 */

export type BreadcrumbCategory = 'navigation' | 'click' | 'fetch' | 'console' | 'custom';

export interface Breadcrumb {
    t: number;                       // timestamp (ms since epoch)
    cat: BreadcrumbCategory;
    msg: string;                     // short human-readable summary
    data?: Record<string, unknown>;  // optional structured payload
}

const MAX_BREADCRUMBS = 20;
const buffer: Breadcrumb[] = [];
let installed = false;

/** Add a breadcrumb to the ring buffer. */
export const addBreadcrumb = (b: Omit<Breadcrumb, 't'>): void => {
    buffer.push({ t: Date.now(), ...b });
    if (buffer.length > MAX_BREADCRUMBS) buffer.shift();
};

/** Get a snapshot of the current buffer (returned copy is safe to serialize). */
export const getBreadcrumbs = (): Breadcrumb[] => buffer.slice();

/** Clear the buffer — call on logout to drop session-specific context. */
export const clearBreadcrumbs = (): void => {
    buffer.length = 0;
};

/* ============================================================
 *  Auto-capture helpers
 * ============================================================ */

const describeClickTarget = (target: EventTarget | null): { tag: string; text: string; testid?: string } => {
    if (!(target instanceof Element)) return { tag: 'unknown', text: '' };

    // Walk up to a meaningful interactive ancestor
    const interactive = target.closest('button, a, [role="button"], [data-testid]') as HTMLElement | null;
    const el = interactive ?? target as HTMLElement;

    const tag = el.tagName.toLowerCase();
    const testid = el.getAttribute('data-testid') || undefined;
    const ariaLabel = el.getAttribute('aria-label');
    const text = (ariaLabel || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
    return { tag, text, testid };
};

const sanitizeUrl = (url: string): string => {
    try {
        const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://x');
        // Keep host + path, drop query (might contain tokens)
        return u.origin + u.pathname;
    } catch {
        return url.length > 200 ? url.slice(0, 200) + '…' : url;
    }
};

const installClickListener = () => {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', (e) => {
        const { tag, text, testid } = describeClickTarget(e.target);
        addBreadcrumb({
            cat: 'click',
            msg: `${tag}${testid ? `[${testid}]` : ''}${text ? ': ' + text : ''}`,
        });
    }, { capture: true, passive: true });
};

const installNavigationListener = () => {
    if (typeof window === 'undefined') return;

    const record = (kind: string) => {
        addBreadcrumb({
            cat: 'navigation',
            msg: `${kind} → ${window.location.pathname}${window.location.search}`,
        });
    };

    // Initial route
    record('initial');

    // Wrap history methods (React Router uses these)
    const wrap = (name: 'pushState' | 'replaceState') => {
        const orig = history[name];
        history[name] = function (this: History, ...args: any[]) {
            const ret = orig.apply(this, args as any);
            // Defer so window.location reflects the new URL
            setTimeout(() => record(name), 0);
            return ret;
        } as any;
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', () => record('popstate'));
};

const installFetchListener = () => {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const start = performance.now();
        const url = typeof input === 'string' ? input
                  : input instanceof URL ? input.toString()
                  : input.url;
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        const safe = sanitizeUrl(url);

        try {
            const res = await originalFetch(input, init);
            const dur = Math.round(performance.now() - start);
            addBreadcrumb({
                cat: 'fetch',
                msg: `${method} ${safe} → ${res.status} (${dur}ms)`,
                data: { status: res.status, ok: res.ok, durationMs: dur },
            });
            return res;
        } catch (err) {
            const dur = Math.round(performance.now() - start);
            addBreadcrumb({
                cat: 'fetch',
                msg: `${method} ${safe} → FAILED (${dur}ms)`,
                data: { failed: true, durationMs: dur },
            });
            throw err;
        }
    };
};

/** Install all auto-capturers. Idempotent — safe to call multiple times. */
export const installBreadcrumbs = (): void => {
    if (installed) return;
    installed = true;
    installClickListener();
    installNavigationListener();
    installFetchListener();
};
